import {
  CircularProgress,
  DialogActions,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { ChangeEvent, useCallback, useState } from 'react';
import Button from './button';
import ModalV2 from './modal-v2';
import Text from './text';
import TextField from './text-field';

const useStyles = makeStyles({
  actions: {
    padding: '32px 0 0',
  },
  comment: {
    marginTop: '16px',
  },
});

interface RemoveAssignmentButtonProps {
  testId?: string;
  isDisabled?: boolean;
  onRemove: (comment: string) => Promise<void>;
}

interface RemoveAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onRemove: (comment: string) => Promise<void>;
}

export function RemoveAssignmentModal({
  open,
  onClose,
  onRemove,
}: RemoveAssignmentModalProps) {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState<string>('');
  const [hasNotesError, setHasNotesError] = useState<boolean>(false);

  const handleRemove = useCallback(async () => {
    try {
      setIsLoading(true);
      await onRemove(notes);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [onRemove, notes]);

  const handleNoteChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNotes(value);
    setHasNotesError(value.length > 100);
  }, []);

  return (
    <ModalV2
      width='480px'
      open={open}
      onClose={onClose}
      id='remove-assignment-modal'
      titleProps={{ title: 'Remove the assignment?' }}>
      <Text variant='normal'>
        Sure you want to remove the assignment? Changes can’t be undone.
        <br />
        <br />
        Add an optional comment explaining the reason for removal
      </Text>
      <TextField
        label='Comment'
        value={notes}
        onChange={handleNoteChange}
        className={classes.comment}
        error={hasNotesError}
      />
      <DialogActions className={classes.actions}>
        <Button
          variant='outlined'
          onClick={onClose}
          data-testid='button-cancel'>
          Cancel
        </Button>
        <Button
          onClick={handleRemove}
          disabled={isLoading || hasNotesError}
          data-testid='button-success'>
          {isLoading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Remove'
          )}
        </Button>
      </DialogActions>
    </ModalV2>
  );
}

export function RemoveAssignmentButton({
  testId,
  isDisabled,
  onRemove,
}: RemoveAssignmentButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <RemoveAssignmentModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onRemove={onRemove}
      />
      <IconButton
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
        data-testid={testId}>
        <DeleteIcon />
      </IconButton>
    </>
  );
}

export default RemoveAssignmentButton;
